import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import API from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import RedactionNotice from "../components/RedactionNotice";
import PrivateNotes from "../components/PrivateNotes";
import NotesThread from "../components/notes/NotesThread";
import { emergencySla, fmtDuration } from "../lib/emergencySla";
import { SlaLadderBeacon, SlaTierBadge } from "../components/EmergencySla";
import EntityFiles from "../components/EntityFiles";
import InHouseOperativeAssigner from "../components/InHouseOperativeAssigner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { FileDropZone } from "../components/FileDropZone";
import { Lightning, ArrowLeft, MapPin, User, Phone, Envelope, Clock, Wrench, PencilSimple, Trash, CheckCircle, Warning, HandWaving, Briefcase, BellRinging } from "@phosphor-icons/react";
import ScopeOfWorksCard from "../components/jobs/ScopeOfWorksCard";
import ScopeOfWorksField from "../components/jobs/ScopeOfWorksField";
import { toast } from "sonner";
import { RaiseInvoiceButton, InvoiceCompletionBanner, LinkedInvoicesCard, useLinkedInvoices, completionToast } from "../components/RaiseInvoicePanel";
import SupplierCrewCard, { SupplierCrewSummary } from "../components/supplier/SupplierCrewCard";
import { CrewEtaBanner } from "../components/RunningLateButton";
import JobFormsPanel, { JobFormsPicker } from "../components/JobFormsPanel";
import { ClientVisibilityBadge, toggleFileVisibility } from "../components/ClientVisibilityBadge";
import AddressAutocomplete from "../components/AddressAutocomplete";
import GetDirectionsButton from "../components/GetDirectionsButton";
import NearbySuppliers from "../components/NearbySuppliers";
import ReassignmentWarning from "../components/ReassignmentWarning";
import SupplierPicker from "../components/SupplierPicker";
import NewSupplierDialog from "../components/NewSupplierDialog";
import What3WordsLink from "../components/What3WordsLink";
import PropertyAccessCard from "../components/PropertyAccessCard";
import AccessNotesField from "../components/AccessNotesField";
import ClientVisibilityToggle from "../components/ClientVisibilityToggle";
import { NoticeBox } from "../components/NoticeBox";
import WoStaleBanner from "../components/jobs/WoStaleBanner";
import { NoticeEditor, EMPTY_NOTICE } from "../components/NoticeEditor";
import SiteReportCard from "../components/voice/SiteReportCard";
import ResidentReportCard from "../components/resident/ResidentReportCard";
import JobHsCard from "../components/hs/JobHsCard";
import CreateRamsButton from "../components/hs/CreateRamsButton";
import RamsFlag from "../components/hs/RamsFlag";
import QuickIssueRams from "../components/hs/QuickIssueRams";
import { useRamsGuard } from "../components/hs/RamsGuard";
import EmailEmergencyBanner from "../components/emergency/EmailEmergencyBanner";

const URGENCY = {
  critical: { label: "Critical", hours: 4, color: "bg-red-600 text-white", textColor: "text-red-600 dark:text-red-400" },
  urgent: { label: "Urgent", hours: 8, color: "bg-orange-500 text-white", textColor: "text-orange-600 dark:text-orange-400" },
  moderate: { label: "Moderate", hours: 12, color: "bg-amber-500 text-white", textColor: "text-amber-600 dark:text-amber-400" },
  minor: { label: "Minor", hours: 24, color: "bg-blue-500 text-white", textColor: "text-blue-600 dark:text-blue-400" },
};
const STATUS_MAP = {
  open: { label: "Open", color: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300" },
  acknowledged: { label: "Acknowledged", color: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300" },
  in_progress: { label: "In Progress", color: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300" },
  completed: { label: "Completed", color: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300" },
  cancelled: { label: "Cancelled", color: "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400" },
};
const STATUSES = ["open", "acknowledged", "in_progress", "completed", "cancelled"];

export default function EmergencyJobDetailPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isVendor = user?.role === "vendor";
  const { invoices: linkedInvoices } = useLinkedInvoices("emergency_job", jobId, !isVendor && user?.role !== "in_house");
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vendors, setVendors] = useState([]);
  const [showEdit, setShowEdit] = useState(false);
  const [showNewSupplier, setShowNewSupplier] = useState(false);
  const [reassigning, setReassigning] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [, setTick] = useState(0);
  const timerRef = useRef(null);

  const fetchJob = useCallback(async () => {
    try {
      const { data } = await API.get(`/emergency-jobs/${jobId}`);
      setJob(data);
    } catch { toast.error("Failed to load job"); navigate("/emergency-jobs"); }
    finally { setLoading(false); }
  }, [jobId, navigate]);

  const { guard: ramsGuard, dialog: ramsGuardDialog } = useRamsGuard({ entityType: "emergency_job", entityId: jobId, hasRams: job ? !!job.rams_id : undefined });

  useEffect(() => {
    // In-house operatives must use their own portal — never the admin emergency-job page.
    if (["in_house", "vendor_operative"].includes(user?.role)) {
      navigate(`/my-work/emergency/${jobId}`, { replace: true });
      return;
    }
    fetchJob();
  }, [fetchJob, user, jobId, navigate]);
  useEffect(() => {
    API.get("/suppliers?limit=500").then(({ data }) => setVendors(Array.isArray(data) ? data : data.vendors || [])).catch(() => {});
  }, []);

  useEffect(() => {
    timerRef.current = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  if (loading || !job) return <div className="text-center py-20 text-muted-foreground">Loading...</div>;

  const u = URGENCY[job.urgency] || URGENCY.urgent;
  const st = STATUS_MAP[job.status] || STATUS_MAP.open;
  const isActive = !["completed", "cancelled"].includes(job.status);
  const sla = emergencySla(job);
  const { elapsed, remaining, pct } = sla;
  const isOverdue = sla.tier === "breached";
  const addr = [job.street_address, job.town_city, job.postcode].filter(Boolean).join(", ");

  const handleStatusChange = async (newStatus) => {
    const previous = job.status;
    const undo = async () => {
      try { await API.put(`/emergency-jobs/${jobId}/status`, { status: previous }); toast.success(`Reverted to ${STATUS_MAP[previous]?.label || previous}`); fetchJob(); }
      catch { toast.error("Could not undo"); }
    };
    try {
      await API.put(`/emergency-jobs/${jobId}/status`, { status: newStatus });
      if (newStatus === "completed" && !isVendor) completionToast(navigate, "emergency_job", jobId, "Emergency job marked as completed", { onUndo: undo, invoice: linkedInvoices.filter(i => i.status !== "cancelled").length === 0 });
      else toast.success(`Status updated to ${STATUS_MAP[newStatus]?.label}`);
      fetchJob();
    } catch { toast.error("Failed to update status"); }
  };

  const handleAssignVendor = async (vendorId) => {
    const vnd = vendors.find(x => x.id === vendorId);
    ramsGuard(async () => {
      try {
        await API.put(`/emergency-jobs/${jobId}`, {
          assigned_vendor_id: vendorId,
          assigned_vendor_name: vnd?.name || vnd?.company_name || "",
        });
        toast.success(`Supplier ${vnd?.company_name || vnd?.name} assigned — urgent email sent`);
        fetchJob();
      } catch { toast.error("Failed to assign"); }
    });
  };

  const handleNewSupplierCreated = async (created, invited) => {
    try {
      await API.put(`/emergency-jobs/${jobId}`, {
        assigned_vendor_id: created.id,
        assigned_vendor_name: created.company_name || created.contact_name || "",
      });
      setVendors(prev => [created, ...prev]);
      toast.success(`${created.company_name} added to your suppliers and assigned — urgent email sent${invited ? " + portal invite sent" : ""}`);
      fetchJob();
    } catch { toast.error("Supplier was added but assigning failed — pick them from the dropdown."); }
  };

  const openEdit = () => {
    setEditForm({ ...job, notice: { ...EMPTY_NOTICE, ...(job.notice || {}) } });
    setShowEdit(true);
  };
  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await API.put(`/emergency-jobs/${jobId}`, editForm);
      toast.success("Emergency job updated");
      setShowEdit(false);
      fetchJob();
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this emergency job?")) return;
    try {
      await API.delete(`/emergency-jobs/${jobId}`);
      toast.success("Emergency job deleted");
      navigate("/emergency-jobs");
    } catch { toast.error("Failed to delete"); }
  };

  const handleManualAcknowledge = async () => {
    const note = window.prompt("Optional note (e.g., 'Confirmed via phone call with supplier'):", "Confirmed via phone call");
    if (note === null) return;
    try {
      await API.post(`/emergency-jobs/${jobId}/acknowledge`, { note });
      toast.success("Job marked as acknowledged");
      fetchJob();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to acknowledge"); }
  };

  return (
    <div data-testid="emergency-job-detail">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/emergency-jobs")} data-testid="emg-back-btn"><ArrowLeft size={16} /></Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <SlaLadderBeacon sla={sla} />
            <span className="text-xs font-mono text-muted-foreground">{job.reference}</span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${u.color}`}>{u.label} — {sla.hours}h SLA</span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${st.color}`}>{st.label}</span>
            <SlaTierBadge sla={sla} testId="emg-detail-sla-badge" />
            {!isVendor && <RamsFlag status={job.rams_status} ramsId={job.rams_id} testId="emg-rams-flag" />}
            {!isVendor && <QuickIssueRams status={job.rams_status} ramsId={job.rams_id} onIssued={fetchJob} className="h-5 px-2 text-[10px] rounded-full" testId="emg-quick-issue-rams" />}
            {isVendor && <RedactionNotice compact />}
            {!isVendor && job.ooh_case_id && <Link to={`/ooh?case=${job.ooh_case_id}`} className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 hover:underline" title="Raised by the Out of Hours desk — the triage record is in the description" data-testid="emg-source-ooh">Out of hours · {job.ooh_case_ref}</Link>}
          </div>
          <h1 className="text-lg sm:text-xl font-bold font-[Chivo] tracking-tight mt-1" data-testid="emg-detail-title">{job.title}</h1>
        </div>
        <div className="flex items-center gap-1">
          {!isVendor && ramsGuardDialog}
          {!isVendor && (
            <>
              <CreateRamsButton entityType="emergency_job" entityId={jobId} testId="emg-create-rams-btn" warn={job.rams_status === "missing"} />
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/work-orders?create=emergency&source_id=${jobId}`)}
                className="bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border-indigo-200 dark:bg-indigo-900/20 dark:text-indigo-300 dark:border-indigo-800/50"
                data-testid="emg-raise-wo-btn"
              >
                <Briefcase size={14} className="mr-1" /> Raise Works Order
              </Button>
              <RaiseInvoiceButton sourceType="emergency_job" sourceId={jobId} testid="emg-raise-invoice-btn" />
              <Button variant="outline" size="sm" onClick={openEdit} data-testid="emg-edit-btn"><PencilSimple size={14} /></Button>
              <Button variant="outline" size="sm" className="text-destructive" onClick={handleDelete} data-testid="emg-delete-btn"><Trash size={14} /></Button>
            </>
          )}
        </div>
      </div>

      <CrewEtaBanner job={job} className="mb-5" />
      {!isVendor && <EmailEmergencyBanner job={job} onEditAddress={openEdit} className="mb-5" />}
      <NoticeBox notice={job.notice} className="mb-5" testid="emg-notice" />
      {!isVendor && <WoStaleBanner flag={job.wo_needs_reissue} className="mb-5" testid="emg-wo-stale-banner" onReissued={fetchJob} />}
      {isVendor && <SupplierCrewCard entityType="emergency_job" entityId={jobId} reference={job.reference} userId={user?._id} className="mb-5" />}
      {!isVendor && user?.role !== "in_house" && <InvoiceCompletionBanner sourceType="emergency_job" sourceId={jobId} reference={job.reference} completed={job.status === "completed"} invoices={linkedInvoices} clientPoNumber={job.client_po_number} onPoSaved={(po) => setJob((j) => ({ ...j, client_po_number: po }))}
        poRequired={!!job.po_required} poRequiredBy={job.po_required_by} poWaived={!!job.po_waived} poWaiverReason={job.po_waiver_reason} onPoWaived={(d) => setJob((j) => ({ ...j, po_waived: d.po_waived, po_waiver_reason: d.po_waiver_reason }))} />}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left: Main content */}
        <div className="lg:col-span-2 space-y-4">
          {!isVendor && user?.role !== "in_house" && <LinkedInvoicesCard sourceType="emergency_job" sourceId={jobId} invoices={linkedInvoices} />}
          {/* Live SLA Timer */}
          <div className={`border-2 rounded-lg p-4 ${sla.tier === "done" ? "border-emerald-300 bg-emerald-50 dark:bg-emerald-950/10" : `bg-card ${sla.ui.box}`}`} data-testid="emg-sla-panel" data-sla-tier={sla.tier}>
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><Clock size={12} /> Response SLA <SlaLadderBeacon sla={sla} size="h-2 w-2" /></h3>
              <span className="flex items-center gap-2">
                <SlaTierBadge sla={sla} testId="emg-sla-panel-badge" />
                <span className={`text-[10px] font-bold ${u.textColor}`}>{sla.hours}h response required</span>
              </span>
            </div>
            {sla.arrived ? (
              <div className="flex items-center justify-between gap-3" data-testid="emg-sla-responded">
                <div>
                  <p className={`text-2xl font-bold font-mono tabular-nums ${sla.ui.text}`} data-testid="emg-elapsed-time">{fmtDuration(elapsed)}</p>
                  <p className="text-[10px] text-muted-foreground">Response time — crew arrived {new Date(job.arrived_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
                </div>
                <div className="text-right">
                  <p className={`text-lg font-bold font-mono tabular-nums ${sla.ui.text}`}>{sla.tier === "met" ? `${fmtDuration(remaining)} to spare` : `${fmtDuration(-remaining)} late`}</p>
                  <p className="text-[10px] text-muted-foreground">{sla.tier === "met" ? "Within SLA" : "Outside SLA"}</p>
                </div>
              </div>
            ) : isActive ? (
              <>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className={`text-2xl font-bold font-mono tabular-nums ${sla.ui.text} ${isOverdue ? "animate-pulse" : ""}`} data-testid="emg-elapsed-time">
                      {fmtDuration(elapsed)}
                    </p>
                    <p className="text-[10px] text-muted-foreground">Time elapsed since creation · {Math.round(pct)}% of SLA window</p>
                  </div>
                  <div className="text-right">
                    <p className={`text-lg font-bold font-mono tabular-nums ${isOverdue ? "text-red-600" : "text-muted-foreground"}`}>
                      {isOverdue ? `+${fmtDuration(-remaining)}` : fmtDuration(remaining)}
                    </p>
                    <p className="text-[10px] text-muted-foreground">{isOverdue ? "Over SLA" : "Remaining"}</p>
                  </div>
                </div>
                <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${sla.ui.bar}`} style={{ width: `${Math.min(100, pct)}%` }} />
                </div>
                <p className="text-[10px] text-muted-foreground mt-2">Amber at 50% · red at 75% · clock stops when the crew arrives on site.</p>
                {job.sla_alerts && Object.keys(job.sla_alerts).length > 0 && (
                  <p className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1 flex-wrap" data-testid="emg-sla-alerts-sent">
                    <BellRinging size={10} /> Admins alerted:{" "}
                    {["amber", "red", "breached"].filter(k => job.sla_alerts[k]).map((k, i) => (
                      <span key={k} className="inline-flex items-center gap-1">
                        {i > 0 && <span aria-hidden="true">·</span>}
                        <span className={`px-1.5 py-0.5 rounded font-medium ${k === "amber" ? "bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200" : "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-200"}`}>
                          {k} {new Date(job.sla_alerts[k]).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </span>
                    ))}
                  </p>
                )}
              </>
            ) : (
              <div className="flex items-center gap-2">
                <CheckCircle size={20} className="text-emerald-500" />
                <p className="text-sm font-medium">{job.status === "completed" ? "Completed" : "Cancelled"}</p>
                {job.completed_at && <span className="text-xs text-muted-foreground ml-auto">at {new Date(job.completed_at).toLocaleString("en-GB")}</span>}
              </div>
            )}
          </div>

          {/* Manual Acknowledge Button — shown only for open (unacknowledged) jobs */}
          {job.status === "open" && !job.acknowledged_at && (
            <div className="border-2 border-amber-400 dark:border-amber-600 rounded-lg p-4 bg-amber-50 dark:bg-amber-950/20" data-testid="emg-acknowledge-panel">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center shrink-0">
                    <HandWaving size={20} className="text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold">Awaiting Acknowledgement</p>
                    <p className="text-xs text-muted-foreground">Supplier hasn't acknowledged yet. Mark manually if confirmed by phone.</p>
                  </div>
                </div>
                <Button
                  onClick={handleManualAcknowledge}
                  className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
                  size="sm"
                  data-testid="emg-manual-acknowledge-btn"
                >
                  <HandWaving size={14} className="mr-1" /> Mark Acknowledged
                </Button>
              </div>
            </div>
          )}

          {/* Scope of Works — add retrospectively (any status), shown to the client + engineer */}
          <ScopeOfWorksCard endpoint={`/emergency-jobs/${jobId}`} value={job.scope_of_works} caveats={job.caveats} notice={job.notice} siteNotes={isVendor ? null : (job.site_notes || "")} ai={isVendor ? null : job.scope_ai} canDraftFromEmail={!isVendor && job.source === "email"} readOnly={isVendor} testid="emg-scope-card"
            context={{ entity: "emergency_job", category: job.category, title: job.title, description: job.description, client_name: job.client_company || job.client_name, property_address: [job.street_address, job.town_city, job.postcode].filter(Boolean).join(", ") }}
            onChanged={(p) => setJob((j) => ({ ...j, ...p }))}
            onSaved={(v, c, n) => setJob((j) => ({ ...j, scope_of_works: v, caveats: c, ...(n ? { notice: n } : {}) }))} />

          {/* Description */}
          {job.description && (
            <div className="border border-border rounded-md p-4">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Description</h3>
              <p className="text-sm whitespace-pre-wrap">{job.description}</p>
            </div>
          )}

          {/* Instructions */}
          {job.instructions && (
            <div className="border border-border rounded-md p-4">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Special Instructions</h3>
              <p className="text-sm whitespace-pre-wrap">{job.instructions}</p>
            </div>
          )}

          {/* Job Sheets — attach forms to emergencies too; suppliers / operatives fill them on site */}
          {(!isVendor || (job.job_form_ids || []).length > 0) && (
            <JobFormsPanel jobId={jobId} jobFormIds={job.job_form_ids || []} canEdit={!isVendor} canFill
              onFormIdsChange={async (ids) => {
                try { await API.put(`/emergency-jobs/${jobId}`, { job_form_ids: ids }); setJob((j) => ({ ...j, job_form_ids: ids })); toast.success("Job sheets updated"); }
                catch (e) { toast.error(e.response?.data?.detail || "Couldn't update job sheets"); }
              }} testId="emg-job-forms" />
          )}

          {/* Property */}
          <div className="border border-border rounded-md p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1"><MapPin size={12} /> Property</h3>
                {job.property_name && <p className="text-sm font-semibold mb-1">{job.property_name}</p>}
                <p className="text-sm">{addr}</p>
                {job.county && <p className="text-xs text-muted-foreground">{job.county}</p>}
                {job.what3words && <div className="mt-1.5"><What3WordsLink words={job.what3words} /></div>}
              </div>
              {(job.latitude || addr) && (
                <GetDirectionsButton
                  address={addr}
                  latitude={job.latitude}
                  longitude={job.longitude}
                  variant="default"
                  size="sm"
                  data-testid="emg-directions-btn"
                />
              )}
            </div>
          </div>

          {/* Status Workflow */}
          {isActive && (
            <div className="border border-border rounded-md p-4" data-testid="emg-status-workflow">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Update Status</h3>
              <div className="flex flex-wrap gap-2">
                {STATUSES.filter(s => s !== job.status).map(s => {
                  const sm = STATUS_MAP[s];
                  return (
                    <Button key={s} variant="outline" size="sm" className="text-xs" onClick={() => handleStatusChange(s)} data-testid={`emg-set-${s}`}>
                      {sm.label}
                    </Button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Timeline */}
          {(job.timeline || []).length > 0 && (
            <div className="border border-border rounded-md p-4">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Timeline</h3>
              <div className="space-y-3">
                {(job.timeline || []).map(t => (
                  <div key={t.id} className="flex gap-3">
                    <div className="w-1.5 bg-border rounded-full shrink-0 mt-1" />
                    <div>
                      <p className="text-xs">{t.description}</p>
                      <p className="text-[10px] text-muted-foreground">{new Date(t.created_at).toLocaleString("en-GB")}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right: Sidebar */}
        <div className="space-y-4">
          <PropertyAccessCard entityType="emergency_job" entityId={jobId} compact />
          {/* Client / Site Contact Info */}
          <div className="border border-border rounded-md p-4">
            {isVendor ? (
              <>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1"><User size={12} /> Site Contact</h3>
                {job.site_contact_name || job.site_contact_phone || job.site_contact_email ? (
                  <>
                    {job.site_contact_name && <p className="text-sm font-semibold">{job.site_contact_name}</p>}
                    {job.site_contact_phone && <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Phone size={10} /> <a href={`tel:${job.site_contact_phone}`} className="hover:underline">{job.site_contact_phone}</a></p>}
                    {job.site_contact_email && <p className="text-xs text-muted-foreground flex items-center gap-1"><Envelope size={10} /> <a href={`mailto:${job.site_contact_email}`} className="hover:underline">{job.site_contact_email}</a></p>}
                  </>
                ) : (
                  <p className="text-xs text-muted-foreground italic">No on-site contact provided. Call the scheduler if you need access details.</p>
                )}
              </>
            ) : (
              <>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1"><User size={12} /> Client</h3>
                {job.client_name && <p className="text-sm font-semibold">{job.client_name}</p>}
                {job.client_company && <p className="text-xs text-muted-foreground">{job.client_company}</p>}
                {job.client_email && <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Envelope size={10} /> {job.client_email}</p>}
                {job.client_phone && <p className="text-xs text-muted-foreground flex items-center gap-1"><Phone size={10} /> {job.client_phone}</p>}
              </>
            )}
          </div>

          {/* Supplier Assignment */}
          {!isVendor && (job.callout_fee > 0 || job.actual_cost > 0) && (
            <div className="border border-border rounded-md p-3 flex items-center justify-between gap-3" data-testid="emg-costs-card">
              <div className="text-xs">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Costs to client</p>
                <p className="mt-0.5">{job.callout_fee > 0 && <span>Call-out £{Number(job.callout_fee).toLocaleString("en-GB", { minimumFractionDigits: 2 })}</span>}{job.callout_fee > 0 && job.actual_cost > 0 && <span> · </span>}{job.actual_cost > 0 && <span>Works £{Number(job.actual_cost).toLocaleString("en-GB", { minimumFractionDigits: 2 })}</span>}</p>
              </div>
              <p className="text-sm font-bold font-[Chivo]">£{(Number(job.callout_fee || 0) + Number(job.actual_cost || 0)).toLocaleString("en-GB", { minimumFractionDigits: 2 })}</p>
            </div>
          )}
          <div className="border border-border rounded-md p-4" data-testid="emg-vendor-panel">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1"><Wrench size={12} /> Assigned Supplier</h3>
            {job.assigned_vendor_name ? (
              <div className="space-y-1" data-testid="emg-assigned-supplier-card">
                {(() => {
                  const sup = vendors.find(v => v.id === job.assigned_vendor_id || (v.user_id && v.user_id === job.assigned_vendor_id));
                  return (
                    <>
                      <p className="text-sm font-semibold">{sup?.company_name || job.assigned_vendor_name}</p>
                      {sup?.contact_name && sup.contact_name !== (sup.company_name || "") && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1.5"><User size={12} /> {sup.contact_name}</p>
                      )}
                      {sup?.trade && <p className="text-[11px] text-muted-foreground">{sup.trade}</p>}
                      {sup?.email && (
                        <a href={`mailto:${sup.email}`} className="flex items-center gap-1.5 text-xs text-primary hover:underline" data-testid="emg-assigned-supplier-email">
                          <Envelope size={12} /> {sup.email}
                        </a>
                      )}
                      {sup?.phone && (
                        <a href={`tel:${sup.phone}`} className="flex items-center gap-1.5 text-xs font-semibold text-red-600 dark:text-red-400 hover:underline" data-testid="emg-assigned-supplier-phone">
                          <Phone size={12} weight="fill" /> {sup.phone}
                        </a>
                      )}
                    </>
                  );
                })()}
                {!isVendor && <SupplierCrewSummary job={job} className="mt-1" />}
                <ReassignmentWarning
                  candidateId={job.assigned_vendor_id || job.assigned_to}
                  entityType="emergency_job"
                  entityId={jobId}
                  trade={job.category || job.trade || ""}
                  area={job.town_city || job.county || job.postcode || ""}
                  onAcceptSuggestion={(s) => handleAssignVendor(s.supplier_user_id || s.supplier_id)}
                  data-testid="emg-reassign-warning"
                />
                {reassigning ? (
                  <div className="pt-2 space-y-2">
                    <SupplierPicker
                      suppliers={vendors}
                      selectedId=""
                      onPick={(s) => { if (s) { handleAssignVendor(s.id); setReassigning(false); } }}
                      label="Search for a replacement supplier"
                      testIdPrefix="emg-reassign-supplier"
                    />
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={() => { setReassigning(false); setShowNewSupplier(true); }} data-testid="emg-reassign-new-supplier-btn">
                        + New supplier
                      </Button>
                      <Button variant="ghost" size="sm" className="text-xs" onClick={() => setReassigning(false)}>Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <Button variant="outline" size="sm" className="text-xs mt-2 w-full" onClick={() => setReassigning(true)} data-testid="emg-change-supplier-btn">
                    Change Supplier
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">No supplier assigned yet</p>
                <SupplierPicker
                  suppliers={vendors}
                  selectedId=""
                  onPick={(s) => { if (s) handleAssignVendor(s.id); }}
                  label="Search your supplier list (name, company or trade)"
                  testIdPrefix="emg-assign-supplier"
                />
                <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => setShowNewSupplier(true)} data-testid="emg-new-supplier-btn">
                  + New supplier (not on system)
                </Button>
              </div>
            )}
            {!isVendor && <ClientVisibilityToggle entityType="emergency_job" entityId={jobId} clientId={job.client_id} value={job.show_contractor_to_client} onChanged={fetchJob} />}
          </div>

          {/* In-House Operative Assignment */}
          {!isVendor && (
            <div className="border border-border rounded-md p-4" data-testid="emg-in-house-block">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1"><Wrench size={12} /> In-House Operative</h3>
              <InHouseOperativeAssigner
                entityType="emergency_job"
                entityId={jobId}
                currentOperativeIds={job.assigned_operative_ids || []}
                currentOperativeNames={job.assigned_operative_names || []}
                currentOperativeId={job.assigned_operative_id || ""}
                currentOperativeName={job.assigned_operative_name || ""}
                hasRams={!!job.rams_id}
                onChange={() => fetchJob()}
              />
            </div>
          )}

          {/* Nearby Suppliers - high urgency picker for emergency jobs */}
          {(job.latitude || job.property_address || job.street_address || job.postcode) && !job.assigned_vendor_name && (
            <NearbySuppliers
              emergencyJobId={jobId}
              trade={job.category || job.trade || ""}
              radiusMiles={30}
              limit={5}
              title="Closest suppliers (emergency)"
              showAutoPick
              includeCompliance
              defaultSort="score"
              onPick={(s) => {
                if (!s.id) {
                  toast.info(`${s.company_name} is not available — please invite them.`);
                  return;
                }
                handleAssignVendor(s.id);
              }}
              data-testid="emg-nearby-suppliers"
            />
          )}

          {/* Attachments */}
          <div className="border border-border rounded-md p-4">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Attachments</h3>
            <FileDropZone renderBadge={!isVendor ? (f, i) => <ClientVisibilityBadge index={i} hidden={f.client_visible === false} onToggle={async () => { const next = toggleFileVisibility(job.attachments || [], i); try { await API.put(`/emergency-jobs/${jobId}`, { attachments: next }); toast.success(next[i].client_visible === false ? "Hidden from the client portal" : "Visible in the client portal"); fetchJob(); } catch { toast.error("Failed to update"); } }} /> : undefined}
              files={job.attachments || []} onFilesChange={async (files) => {
              try {
                await API.put(`/emergency-jobs/${jobId}`, { attachments: files });
                fetchJob();
              } catch { toast.error("Failed to update attachments"); }
            }} folder="emergency" accept="all" maxFiles={20} compact />
          </div>

          {/* Info */}
          <div className="border border-border rounded-md p-4 text-xs text-muted-foreground space-y-1">
            <p>Created by: <span className="font-medium text-foreground">{job.created_by_name}</span></p>
            <p>Created: <span className="font-medium text-foreground">{new Date(job.created_at).toLocaleString("en-GB")}</span></p>
            {job.acknowledged_at && <p>Acknowledged: <span className="font-medium text-foreground">{new Date(job.acknowledged_at).toLocaleString("en-GB")}</span></p>}
            {job.completed_at && <p>Completed: <span className="font-medium text-foreground">{new Date(job.completed_at).toLocaleString("en-GB")}</span></p>}
          </div>

          {/* File Hub — central document/media storage for this emergency job */}
          <div className="bg-card border border-border rounded-lg p-4" data-testid="emergency-job-files-section">
            <h3 className="text-base font-semibold mb-3">Files &amp; Documents</h3>
            <EntityFiles entityType="emergency_job" entityId={jobId} currentUser={user} />
          </div>

          {!isVendor && <JobHsCard entityType="emergency_job" entityId={jobId} />}
          {!isVendor && <SiteReportCard entityType="emergency_job" entityId={jobId} />}
          {!isVendor && <ResidentReportCard entityType="emergency_job" entityId={jobId} />}

          {/* Notes — same shared notes thread as requests/jobs */}
          {!isVendor && (
            <div className="bg-card border border-border rounded-lg p-4" data-testid="emergency-notes-section">
              <NotesThread entityType="emergency" entityId={jobId} onChanged={fetchJob} folder="emergency-notes" />
            </div>
          )}

          {/* Private Notes — admins only */}
          {!isVendor && <PrivateNotes entityType="emergency_job" entityId={jobId} />}
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Edit Emergency Job</DialogTitle>
            <DialogDescription>Update job details</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input value={editForm.title || ""} onChange={e => setEditForm({ ...editForm, title: e.target.value })} className="mt-1" /></div>
            <div><Label>Description</Label><Textarea value={editForm.description || ""} onChange={e => setEditForm({ ...editForm, description: e.target.value })} rows={2} className="mt-1" /></div>
            <div><Label>Instructions</Label><Textarea value={editForm.instructions || ""} onChange={e => setEditForm({ ...editForm, instructions: e.target.value })} rows={2} className="mt-1" /></div>
            <ScopeOfWorksField value={editForm.scope_of_works} onChange={(v) => setEditForm(f => ({ ...f, scope_of_works: v }))} testid="edit-emg-scope-of-works" rows={4}
              context={{ entity: "emergency_job", title: editForm.title, description: editForm.description, client_name: editForm.client_company || editForm.client_name, property_address: [editForm.street_address, editForm.town_city, editForm.postcode].filter(Boolean).join(", ") }} />
            <NoticeEditor value={editForm.notice} onChange={(n) => setEditForm({ ...editForm, notice: n })} clientToggle aiContext={{ entity: "emergency_job", title: editForm.title, description: editForm.description }} testid="edit-emg-notice" compact />
            <JobFormsPicker value={editForm.job_form_ids || []} onChange={(ids) => setEditForm({ ...editForm, job_form_ids: ids })} testId="emg-edit-job-forms" />
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Call-out fee (£)</Label><Input type="number" min="0" step="0.01" value={editForm.callout_fee ?? ""} onChange={e => setEditForm({ ...editForm, callout_fee: e.target.value })} className="mt-1" placeholder="0.00" data-testid="emg-edit-callout-fee" /></div>
              <div><Label>Cost to client (£)</Label><Input type="number" min="0" step="0.01" value={editForm.actual_cost ?? ""} onChange={e => setEditForm({ ...editForm, actual_cost: e.target.value })} className="mt-1" placeholder="Works / parts / labour" data-testid="emg-edit-actual-cost" /></div>
            </div>
            <p className="text-[10px] text-muted-foreground -mt-1">These pre-fill the client invoice when you raise it from this emergency.</p>
            <AddressAutocomplete
              label="Search address"
              placeholder="Start typing postcode or address..."
              onSelect={(a) => setEditForm(f => ({
                ...f,
                street_address: a.street_address || f.street_address,
                town_city: a.town_city || f.town_city,
                county: a.county || f.county,
                postcode: a.postcode || f.postcode,
                latitude: a.latitude || null,
                longitude: a.longitude || null,
              }))}
              data-testid="emg-edit-address-autocomplete"
            />
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Street</Label><Input value={editForm.street_address || ""} onChange={e => setEditForm({ ...editForm, street_address: e.target.value })} className="mt-1" /></div>
              <div><Label>Town/City</Label><Input value={editForm.town_city || ""} onChange={e => setEditForm({ ...editForm, town_city: e.target.value })} className="mt-1" /></div>
              <div><Label>Postcode</Label><Input value={editForm.postcode || ""} onChange={e => setEditForm({ ...editForm, postcode: e.target.value })} className="mt-1" /></div>
              <div><Label>Category</Label><Input value={editForm.category || ""} onChange={e => setEditForm({ ...editForm, category: e.target.value })} className="mt-1" /></div>
              <div className="col-span-2"><Label>what3words <span className="text-[10px] text-muted-foreground">(optional)</span></Label><Input value={editForm.what3words || ""} onChange={e => setEditForm({ ...editForm, what3words: e.target.value })} placeholder="///filled.count.soap" className="mt-1 font-mono" data-testid="edit-emg-what3words" /></div>
              <div className="col-span-2"><AccessNotesField value={editForm.access_notes || ""} onChange={(v) => setEditForm(f => ({ ...f, access_notes: v }))} propertyLinked={!!job.property_id} testid="emg-edit-access-notes" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEdit(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={saving}>{saving ? "Saving..." : "Save Changes"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New off-system supplier — add, assign & optionally invite to the portal */}
      <NewSupplierDialog
        open={showNewSupplier}
        onClose={() => setShowNewSupplier(false)}
        onCreated={handleNewSupplierCreated}
        testIdPrefix="emg-new-supplier"
      />
    </div>
  );
}
